import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { useToast } from "@/shared/hooks/use-toast";
import { apiErrorCode, resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { useIntegrationBaseCache } from "../hooks/useIntegrationBaseCache";
import { affectedSkillCount, countToolsDiff, defaultDiffSelection, isToolsDiffEmpty } from "../utils/toolsDiff";
import type { IntegrationBase, IntegrationTool, McpRefreshPreview, ToolsDiff } from "../types/integration";

interface IntegrationToolsDiffDialogProps {
  isOpen: boolean;
  /** `template`: instalação × template (conector de API). `refresh`: snapshot × servidor (MCP, AB#87477). */
  mode: "template" | "refresh";
  integration: IntegrationBase;
  onClose: () => void;
}

type Bucket = "added" | "removed" | "changed";

/**
 * Instalação × template atual, antes de aceitar (AB#87542). Nada é gravado ao abrir: a
 * prévia é um GET. Cada tool tem a sua caixa — a organização pode ter mudado uma de
 * propósito e querer só as outras.
 */
export const IntegrationToolsDiffDialog = ({ isOpen, mode, integration, onClose }: IntegrationToolsDiffDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const applyUpdated = useIntegrationBaseCache();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isApplying, setIsApplying] = useState(false);

  const { data: diff, isLoading, error, refetch } = useQuery({
    queryKey: ["integration-tools-diff", mode, integration.id],
    queryFn: (): Promise<ToolsDiff | McpRefreshPreview> =>
      mode === "template"
        ? IntegrationBaseService.getTemplateDiff(integration.id)
        : IntegrationBaseService.previewMcpRefresh(integration.id),
    enabled: isOpen,
    // Sempre a situação de agora: o template e o servidor mudam por fora.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  // Por padrão, só o que o template trouxe; o que existe só aqui fica para a pessoa decidir.
  useEffect(() => {
    if (diff) setSelected(new Set(defaultDiffSelection(diff)));
  }, [diff]);

  const counts = countToolsDiff(diff);
  const empty = isToolsDiffEmpty(diff);
  const affected = diff && "affected_skills" in diff ? diff : null;
  const skills = useMemo(
    () => (affected?.affected_skills ?? []).filter((skill) => skill.tools.length > 0),
    [affected],
  );

  const toggle = (name: string, checked: boolean) =>
    setSelected((atual) => {
      const next = new Set(atual);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const updated =
        mode === "template"
          ? await IntegrationBaseService.applyTemplateDiff(integration.id, Array.from(selected))
          : await IntegrationBaseService.applyMcpRefresh(
              integration.id,
              diff && "tools_hash" in diff ? diff.tools_hash : undefined,
            );
      await applyUpdated(updated, integration.id);
      toast({ title: t("integrations.tools.diff.applied") });
      onClose();
    } catch (err) {
      if (apiErrorCode(err) === "E_MCP_TOOLS_CHANGED") {
        // O servidor mudou a lista desde a prévia: mostra o diff novo em vez de aplicar às cegas.
        toast({ title: t("integrations.tools.diff.serverChanged"), variant: "destructive" });
        await refetch();
        return;
      }
      toast({
        title: t("common.error"),
        description: resolveApiErrorMessage(err, t),
        variant: "destructive",
      });
    } finally {
      setIsApplying(false);
    }
  };

  const renderTool = (bucket: Bucket, tool: IntegrationTool, name: string, previous?: IntegrationTool) => (
    <li key={`${bucket}-${name}`} className="flex items-start gap-3 rounded-md border px-3 py-2">
      {/* No refresh o servidor é a fonte: aceita-se o diff inteiro, sem escolher tool a tool. */}
      {mode === "template" && (
        <Checkbox
          checked={selected.has(name)}
          onCheckedChange={(checked) => toggle(name, checked === true)}
          className="mt-0.5"
          aria-label={name}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-medium">{name}</span>
          <Badge
            variant={bucket === "removed" ? "destructive" : bucket === "added" ? "success" : "secondary"}
            className="text-[10px]"
          >
            {/* No refresh, "removida" quer dizer que saiu do servidor; no template, que só existe aqui. */}
            {mode === "refresh" && bucket === "removed"
              ? t("integrations.tools.diff.removedFromServer")
              : t(`integrations.tools.diff.${bucket}`)}
          </Badge>
        </div>
        {tool.description && <p className="line-clamp-2 text-xs text-muted-foreground">{tool.description}</p>}
        {previous && previous.description !== tool.description && previous.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground line-through">{previous.description}</p>
        )}
      </div>
    </li>
  );

  const canApply = !empty && !isApplying && (mode === "refresh" || selected.size > 0);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-xl flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === "template" ? t("integrations.tools.diff.templateTitle") : t("integrations.tools.diff.refreshTitle")}
          </DialogTitle>
          <DialogDescription>
            {mode === "template"
              ? t("integrations.tools.diff.templateDescription")
              : t("integrations.tools.diff.refreshDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="scroll-slim min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{resolveApiErrorMessage(error, t)}</p>
          ) : empty ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("integrations.tools.diff.none")}</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {t(mode === "refresh" ? "integrations.tools.diff.refreshSummary" : "integrations.tools.diff.summary", { ...counts })}
              </p>

              {mode === "refresh" && skills.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="space-y-1">
                    <p>{t("integrations.tools.diff.affectedSkills", { count: affectedSkillCount(affected) })}</p>
                    <ul className="list-disc pl-4 text-xs">
                      {skills.map((skill) => (
                        <li key={skill.name}>
                          <span className="font-medium">{skill.name}</span>: {skill.tools.join(", ")}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <ul className="space-y-2">
                {(diff?.added ?? []).map((tool) => renderTool("added", tool, tool.name))}
                {(diff?.changed ?? []).map((change) =>
                  renderTool(
                    "changed",
                    change.after,
                    change.name ?? change.after?.name ?? change.before?.name,
                    change.before,
                  ),
                )}
                {(diff?.removed ?? []).map((tool) => renderTool("removed", tool, tool.name))}
              </ul>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isApplying}>
            {t("integrations.actions.cancel")}
          </Button>
          <Button onClick={() => void handleApply()} disabled={!canApply}>
            {isApplying && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {mode === "template" ? t("integrations.tools.diff.applySelected") : t("integrations.tools.diff.acceptRefresh")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
